'use client';

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { isLocale, persistLocalePreference } from '@/lib/localePreference';

const LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

export function LanguageSelector() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function switchLocale(code: string) {
    // Persiste la scelta (localStorage + cookie NEXT_LOCALE) cosi' sopravvive
    // al giro di auth Clerk e ai link/redirect senza prefisso locale.
    if (isLocale(code)) persistLocalePreference(code);
    router.replace(pathname, { locale: code });
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-stone-500 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-zinc-100 hover:bg-stone-100 dark:hover:bg-zinc-900 rounded-md transition-colors"
        aria-label="Change language"
        aria-expanded={open}
      >
        <Globe size={14} />
        <span className="text-[12px] font-medium tracking-wide uppercase">{locale}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-stone-50 dark:bg-zinc-900 border border-stone-200 dark:border-zinc-800 rounded-[10px] py-1 min-w-[160px] shadow-lg">
          {LANGUAGES.map(({ code, name, flag }) => (
            <button
              key={code}
              onClick={() => switchLocale(code)}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm hover:bg-stone-100 dark:hover:bg-zinc-800 transition-colors ${
                code === locale
                  ? 'text-stone-950 dark:text-zinc-50 font-semibold'
                  : 'text-stone-600 dark:text-zinc-400'
              }`}
            >
              <span>{flag}</span>
              <span>{name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
